import { supabase } from '@/integrations/supabase/client';
import { BaseAttachedResearch, EntityConfig } from '@/types/research-attachments';
import { buildSelectQuery, buildInsertData } from '../utils/query-builders';
import { calculateNextAttachmentOrder } from '../utils/attachment-order';

/**
 * Attaches research to an entity
 */
export const attachResearch = async <T extends BaseAttachedResearch>(
  config: EntityConfig,
  entityId: string,
  researchId: string
): Promise<T> => {
  // DISABLED: Workbook research system not implemented
  if (config.entityType === 'workbook') {
    console.log('[attachResearch] Workbook research disabled - operation not allowed');
    throw new Error('Workbook research system is currently disabled');
  }

  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    throw new Error('You must be logged in to attach research');
  }

  let attachmentOrder: number | undefined;

  // Calculate attachment order for page entities
  if (config.entityType === 'page') {
    try {
      const { data: existingAttachments, error: fetchError } = await supabase
        .from(config.tableName as any)
        .select('attachment_order')
        .eq(config.entityIdColumn, entityId)
        .order('attachment_order', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('Error fetching existing attachments:', fetchError);
        attachmentOrder = 1;
      } else {
        attachmentOrder = calculateNextAttachmentOrder(existingAttachments || []);
      }
    } catch (error) {
      console.error('Error calculating attachment order:', error);
      attachmentOrder = 1;
    }
  }

  const insertData = buildInsertData(config, entityId, researchId, user.id, attachmentOrder);
  const selectQuery = buildSelectQuery(config);

  const { data, error } = await supabase
    .from(config.tableName as any)
    .insert(insertData)
    .select(selectQuery)
    .single();

  if (error) {
    console.error(`Error attaching research to ${config.entityType}:`, error);
    throw error;
  }

  if (!data) {
    throw new Error(`Failed to attach research - no data returned`);
  }

  return data as unknown as T;
};
